import type { ExternalRegisterAlertType } from "@/components/externalregister";
import i18n from "@/i18n";

export type ImportTargetField =
  | "surname"
  | "givenName"
  | "aliases"
  | "sex"
  | "dob"
  | "umrn"
  | "type"
  | "hospital"
  | "dateListed"
  | "comments";

export type ImportFieldDefinition = {
  field: ImportTargetField;
  label: string;
  required: boolean;
};

export const IMPORT_TARGET_FIELDS: ImportFieldDefinition[] = [
  { field: "surname", label: i18n.t("externalRegister.import.fieldLabels.surname", { defaultValue: "Surname" }), required: true },
  { field: "givenName", label: i18n.t("externalRegister.import.fieldLabels.givenName", { defaultValue: "Given name" }), required: true },
  { field: "type", label: i18n.t("externalRegister.import.fieldLabels.type", { defaultValue: "Type" }), required: true },
  { field: "hospital", label: i18n.t("externalRegister.import.fieldLabels.hospital", { defaultValue: "Hospital" }), required: true },
  { field: "dateListed", label: i18n.t("externalRegister.import.fieldLabels.dateListed", { defaultValue: "Date listed" }), required: true },
  { field: "aliases", label: i18n.t("externalRegister.import.fieldLabels.aliases", { defaultValue: "Aliases" }), required: false },
  { field: "sex", label: i18n.t("externalRegister.import.fieldLabels.sex", { defaultValue: "Sex" }), required: false },
  { field: "dob", label: i18n.t("externalRegister.import.fieldLabels.dob", { defaultValue: "DOB" }), required: false },
  { field: "umrn", label: i18n.t("externalRegister.import.fieldLabels.umrn", { defaultValue: "UMRN" }), required: false },
  { field: "comments", label: i18n.t("externalRegister.import.fieldLabels.comments", { defaultValue: "Comments" }), required: false },
];

export const REQUIRED_IMPORT_TARGET_FIELDS = IMPORT_TARGET_FIELDS
  .filter((definition) => definition.required)
  .map((definition) => definition.field);

export const EMPTY_IMPORT_MAPPING: ImportMapping = {
  surname: null,
  givenName: null,
  aliases: null,
  sex: null,
  dob: null,
  umrn: null,
  type: null,
  hospital: null,
  dateListed: null,
  comments: null,
};

export type ImportMapping = Record<ImportTargetField, number | null>;

export type ImportParseRequest = {
  clientImportId: string;
  csvText: string;
};

export type ImportParseResponse = {
  clientImportId: string;
  sourceFingerprint: string;
  status: "parsed";
  rowCount: number;
  columnCount: number;
  hasHeaderGuess: boolean;
  columns: Array<{ index: number; label: string; sampleValues: string[] }>;
  sampleRows: string[][];
  parseErrors: Array<{ rowNumber: number; message: string }>;
};

export type ImportValidateRequest = {
  clientImportId: string;
  csvText: string;
  hasHeader: boolean;
  mapping: ImportMapping;
};

export type ImportMatchConfidence = "HIGH" | "MEDIUM" | "LOW" | "ERROR";
export type ImportReviewPriority = "READY" | "NEEDS_REVIEW" | "ERROR";
/**
 * Granular basis for the preview decision. Use this for filtering, bulk review,
 * and operator-facing match-strength labels.
 */
export type ImportMatchBasis =
  | "UMRN"
  | "NAME_DOB_SEX"
  | "NAME_DOB"
  | "NAME_SEX"
  | "NAME_ONLY"
  | "CREATED_NEW"
  | "ERROR";

export type ImportMatchPreview = {
  /**
   * Legacy broad match category kept for API/backward compatibility.
   * Demographic matches remain grouped as NAME_DOB_SEX here even when
   * matchBasis carries the more precise NAME_DOB, NAME_SEX, or NAME_ONLY value.
   */
  matchedOn: "UMRN" | "NAME_DOB_SEX" | "CREATED_NEW" | "ERROR";
  /**
   * Precise review basis for current UI logic. Optional in the portal so older
   * persisted drafts/responses can still be read and mapped from matchedOn.
   */
  matchBasis?: ImportMatchBasis;
  patientId?: string;
  candidateCount?: number;
  candidates?: Array<{
    patientId: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    sex?: string;
    umrn?: string;
    medicareNo?: string;
    medicareNoPos?: string;
    phone?: string;
    email?: string;
  }>;
};

export type ImportPreviewRow = {
  rowId: string;
  rowNumber: number;
  status: "valid" | "error";
  mappedRecord?: Partial<{
    surname: string;
    givenName: string;
    aliases: string[];
    sex: string;
    dob: string;
    umrn: string;
    type: ExternalRegisterAlertType;
    hospital: string;
    dateListed: string;
    comments: string;
  }>;
  errors: string[];
  matchConfidence: ImportMatchConfidence;
  reviewPriority: ImportReviewPriority;
  reviewReasons: string[];
  matchPreview?: ImportMatchPreview;
  commitError?: string;
};

export type ImportValidateResponse = {
  clientImportId: string;
  sourceFingerprint: string;
  status: "validated" | "failed";
  totalRows: number;
  validRows: number;
  errorRows: number;
  needsReviewRows: number;
  readyRows: number;
  createsNewPatientRows: number;
  updateOrReactivationRows: number;
  preview: ImportPreviewRow[];
};

export type ImportCommitStatus =
  | "committing"
  | "completed"
  | "completed_with_errors"
  | "failed";

export type ImportCommitRequest = {
  clientImportId: string;
  sourceFingerprint: string;
  csvText: string;
  hasHeader: boolean;
  mapping: ImportMapping;
  approvedRowIds?: string[];
  excludedRowIds?: string[];
  commitRowIds?: string[];
  selectedPatientIds?: Record<string, string>;
};

export type ImportCommitResponse = {
  clientImportId: string;
  sourceFingerprint: string;
  status: ImportCommitStatus;
  simulated?: boolean;
  batchSize: number;
  totalBatches: number;
  currentBatch: number;
  totalRecords: number;
  successfullyImported: number;
  errors: number;
  skippedOrExcluded: number;
  retryableRows: number;
  matchedOnUmrn: number;
  matchedOnDemographics: number;
  newPatientsCreated: number;
  updatedExistingEntries: number;
  batches: Array<{
    batchNumber: number;
    status: "pending" | "committing" | "completed" | "completed_with_errors" | "failed";
    attemptedRows: number;
    committedRows: number;
    failedRows: number;
    errors: Array<{ rowId?: string; rowNumber?: number; message: string }>;
  }>;
  rows: Array<{
    rowId: string;
    rowNumber: number;
    patientName?: string;
    status: "MATCHED_UMRN" | "MATCHED_DEMOGRAPHICS" | "CREATED_NEW" | "UPDATED_EXISTING" | "SKIPPED" | "ERROR";
    matchConfidence: ImportMatchConfidence;
    reviewPriority: ImportReviewPriority;
    reviewReasons: string[];
    patientId?: string;
    externalRegisterEntryId?: string;
    errorMessage?: string;
  }>;
};

export type ImportPartitionKind = "HIGH" | "MEDIUM" | "LOW" | "ERROR";

export type ExternalRegisterImportAdapter = {
  parse: (request: ImportParseRequest) => Promise<ImportParseResponse>;
  validate: (request: ImportValidateRequest) => Promise<ImportValidateResponse>;
  commit: (request: ImportCommitRequest) => Promise<ImportCommitResponse[]>;
};

export type ImportReviewFilter =
  | "all"
  | "needsReview"
  | "ready"
  | "errors"
  | "matched"
  | "createsNew"
  | "matchUmrn"
  | "matchNameDobSex"
  | "matchNameDob"
  | "matchNameSex"
  | "matchNameOnly";
